import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { PapiDeviation } from '@wix/da-papi-types';
import DividerGhostButton from '@wix/da-ds/pkg/Buttons/DividerGhostButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import QuestionCard from './_partials/QuestionCard';
import {
  OriginalDeviationFieldName,
  OriginalDeviationFormFields,
  Question,
  QuestionFieldName,
  QuestionFormFields,
  QuestionWithEditorState,
} from '../../types';
import QuestionForm from './_partials/QuestionForm';
import { EditorState } from '@wix/da-editor-tiptap/pkg/components/Editor/types';
import { fromDocument } from '@wix/da-editor-tiptap/pkg/components/utils/helpers';

import s from './SecondStep.scss';

interface Props {
  deviation?: PapiDeviation;
  hasError?: boolean;
}

const SecondStep: React.FC<Props> = ({ hasError }) => {
  const { t } = useTranslation();
  const [addingQuestionPosition, setAddingQuestionPosition] = useState<
    number | null
  >(null);
  const [editingQuestionPosition, setEditingQuestionPosition] = useState<
    number | null
  >(null);
  const { control } = useFormContext<OriginalDeviationFormFields>();
  const {
    field: { value: questions, onChange },
  } = useController({
    name: OriginalDeviationFieldName.QUESTIONS,
    control,
  });

  const formDataToQuestion = (
    formData: QuestionFormFields
  ): QuestionWithEditorState => {
    const questionEditor = formData[QuestionFieldName.QUESTION_BODY];
    const editorState =
      typeof questionEditor === 'function'
        ? (JSON.parse(questionEditor()) as EditorState)
        : undefined;

    // Pre-sanitise answers to remove empty prices
    const answers = formData[QuestionFieldName.ANSWER_CHOICES];
    if (answers && answers.length > 0) {
      answers.forEach((value, index) => {
        if (`${value.priceModifier}` === '') {
          delete value.priceModifier;
        }
        answers[index] = value;
      });
    }

    return {
      type: formData[QuestionFieldName.QUESTION_TYPE],
      isRequired: formData[QuestionFieldName.IS_REQUIRED],
      answers,
      question: editorState?.document && fromDocument(editorState?.document),
      questionEditor,
    };
  };

  const addQuestion = useCallback(
    (question: QuestionFormFields) => {
      if (addingQuestionPosition === null) {
        return;
      }

      const newQuestions = [...questions];
      newQuestions.splice(
        addingQuestionPosition,
        0,
        formDataToQuestion(question)
      );
      onChange(newQuestions);
      setAddingQuestionPosition(null);
    },
    [questions, addingQuestionPosition, onChange]
  );

  const updateQuestion = useCallback(
    (position: number, formData: QuestionFormFields) => {
      onChange(
        questions.map((question, idx) =>
          idx === position ? formDataToQuestion(formData) : question
        )
      );
      setEditingQuestionPosition(null);
    },
    [questions, onChange]
  );

  const removeQuestion = useCallback(
    (position: number) => {
      onChange(questions.filter((_, idx) => idx !== position));
    },
    [questions, onChange]
  );

  const moveQuestion = useCallback(
    (currentPosition: number, newPosition: number) => {
      const newQuestions = [...questions];
      const [movedQuestion] = newQuestions.splice(currentPosition, 1);
      newQuestions.splice(newPosition, 0, movedQuestion);
      onChange(newQuestions);
    },
    [questions, onChange]
  );

  const onAddQuestionClick = useCallback((position: number) => {
    if (addingQuestionPosition === null) {
      setAddingQuestionPosition(position);
    }
  }, [addingQuestionPosition]);

  const onEditQuestionClick = useCallback(
    (position: number) => {
      if (addingQuestionPosition === null) {
        setEditingQuestionPosition(position);
      }
    },
    [addingQuestionPosition]
  );

  const isFormOpen =
    addingQuestionPosition !== null ||
    editingQuestionPosition !== null;

  const renderQuestionFormOrButton = (position: number) => (
    <>
      {addingQuestionPosition === position ? (
        <QuestionForm
          onSave={addQuestion}
          onDiscard={() => setAddingQuestionPosition(null)}
        />
      ) : (
        <DividerGhostButton
          onClick={() => onAddQuestionClick(position)}
          size="medium"
          disabled={isFormOpen}
          icon={AddIcon}
        >
          {t('originalDeviationPopup.questionnaire.addQuestion')}
        </DividerGhostButton>
      )}
    </>
  );

  return (
    <div className={s['root']}>
      <p className={s['subheading']}>
        {t('originalDeviationPopup.questionnaire.subheading')}
      </p>
      {renderQuestionFormOrButton(0)}
      {hasError && !questions.length && (
        <p className={s['error']}>
          {t('originalDeviationPopup.questionnaire.error')}
        </p>
      )}

      <div className={s['questions']}>
        {questions.map((question: Question, idx: number) => (
          <div className={s['question']} key={`question-${idx}`}>
            {editingQuestionPosition === idx ? (
              <QuestionForm
                question={question}
                onSave={formData => updateQuestion(idx, formData)}
                onDiscard={() => setEditingQuestionPosition(null)}
              />
            ) : (
              <QuestionCard
                question={question}
                hasMoveUp={idx > 0}
                hasMoveDown={idx < questions.length - 1}
                onMoveUpClick={() => moveQuestion(idx, idx - 1)}
                onMoveDownClick={() => moveQuestion(idx, idx + 1)}
                onEditClick={() => onEditQuestionClick(idx)}
                onRemoveClick={() => removeQuestion(idx)}
                disableActions={isFormOpen}
              />
            )}
            {renderQuestionFormOrButton(idx + 1)}
          </div>
        ))}
      </div>
    </div>
  );
};
SecondStep.displayName = 'SecondStep';

export default SecondStep;
